import { useCallback, useMemo, useState } from 'react';
import { mergeCommandDraft } from 'features/Integrations/utils/devices.utils';

// Seed once per selected group, including when its devices first finish loading.
// A refresh supplies live display data and keeps only the fields the operator edited.
const useGroupCommandDraft = (groupId, seed) => {
  const [state, setState] = useState(() => ({
    groupId,
    draft: seed,
    draftSeed: seed,
    editedFields: [],
  }));
  let current = state;
  if (state.groupId !== groupId || (!state.draft && seed)) {
    current = { groupId, draft: seed, draftSeed: seed, editedFields: [] };
    setState(current);
  }

  // draftSeed records the live data the draft was built from, so the draft is merged
  // again only when new live data arrives, not on every render.
  const setDraft = useCallback(
    (draft, editedFields = []) => {
      setState((previous) =>
        previous.groupId === groupId
          ? {
              groupId,
              draft,
              draftSeed: seed,
              editedFields: editedFields.length
                ? [...new Set([...previous.editedFields, ...editedFields])]
                : previous.editedFields,
            }
          : previous,
      );
    },
    [groupId, seed],
  );

  // Call when a send starts and call the returned function when it succeeds. The controls then
  // follow the live group again for the fields that were sent; edits made while the send is
  // pending are kept.
  const startCommandSend = () => {
    const sentFields = current.editedFields;
    return () =>
      setState((previous) =>
        previous.groupId === groupId
          ? {
              ...previous,
              editedFields: previous.editedFields.filter((field) => !sentFields.includes(field)),
            }
          : previous,
      );
  };

  const draft = useMemo(() => {
    if (!seed || !current.draft) return null;
    if (current.draftSeed === seed) return current.draft;
    return mergeCommandDraft(seed, current.draft, current.editedFields);
  }, [seed, current.draft, current.draftSeed, current.editedFields]);

  return [draft, setDraft, startCommandSend];
};

export default useGroupCommandDraft;
